<template>
  <Container>
    <UserCard
      :user="user"
    />
  </Container>

  <!-- global stats -->
  <Container
    class="server-global"
  >
    <StatChips
      :stats="[
        {
          icon: 'right-to-bracket',
          label: t('jc.view.user.stat.global.firstJoinMoment'),
          value: user?.globalFirstJoinMoment ? d(user?.globalFirstJoinMoment || 0, 'short') : undefined,
        },
        {
          icon: 'timer',
          label: t('jc.view.user.stat.global.previousPlaytime'),
          value: user?.globalPreviousPlaytime ? formatPlaytime(user.globalPreviousPlaytime) : undefined,
        },
        {
          icon: 'right-from-bracket',
          label: t('jc.view.user.stat.global.lastQuitMoment'),
          value: user?.globalLastQuitMoment ? d(user?.globalLastQuitMoment || 0, 'short') : undefined,
        },
        {
          icon: 'angles-up',
          label: t('jc.view.user.stat.global.level'),
          value: user?.level,
        },
        {
          icon: 'check-to-slot',
          label: t('jc.view.user.stat.global.votes'),
          value: userVote?.votes,
        },
        {
          icon: 'bars-staggered',
          label: t('jc.view.user.stat.global.voteStreak'),
          value: userVote?.streak,
        },
      ]"
    />
  </Container>

  <!-- dungeon stats -->
  <Container
    class="server-dungeon"
  >
    <Heading
      :icon="'dungeon'"
      :label="t('jc.view.user.section.dungeon.title')"
    />
    <StatChips
      :stats="[
        {
          icon: 'right-to-bracket',
          label: t('jc.view.user.stat.dungeon.firstJoinMoment'),
          value: userDungeon?.serverFirstJoinMoment ? d(userDungeon?.serverFirstJoinMoment || 0, 'short') : undefined,
        },
        {
          icon: 'timer',
          label: t('jc.view.user.stat.dungeon.previousPlaytime'),
          value: userDungeon?.serverPreviousPlaytime ? formatPlaytime(userDungeon.serverPreviousPlaytime) : undefined,
        },
      ]"
    />
    <StatTables
      v-if="dungeonEnvironments"
      :tables="dungeonEnvironments
        .filter(env => userDungeon?.campaignProgress?.[env.identifier])
        .map(env => ({
          title: t(`jc.dungeon.environment.${env.identifier}.title`),
          background: {
            src: `images/view/user/_user/dungeon/environment/${env.identifier}.png`,
          },
          stats: [
            ...['NORMAL', 'HARD', 'DEADLY'].map(difficulty => ({
              label: t(`jc.view.user.stat.dungeon.campaignProgress.${difficulty}`),
              value: progress(userDungeon?.campaignProgress?.[env.identifier]?.[difficulty]),
            })),
            {
              label: t(`jc.view.user.stat.dungeon.endlessProgress`),
              value: userDungeon?.endlessProgress?.[env.identifier],
            },
            {
              label: t(`jc.view.user.stat.dungeon.arenaProgress`),
              value: userDungeon?.arenaProgress?.[env.identifier],
            },
          ],
        }))
      "
    />
  </Container>
</template>

<script setup lang="ts">
import type { FetchError } from 'ofetch'
import type {
  User,
  DungeonEnvironment, UserDungeon, UserVote,
} from '~/composables/charon'
import { useMoment } from '~/composables/useMoment'

const { t, d } = useI18n()
const route = useRoute()
const { charon } = useCharon()
const { moment } = useMoment()

definePageMeta({
  nameKey: 'user',
  ogType: 'profile',
})

function progress(value: Record<string, number> | undefined): string | undefined {
  if (!value) return undefined
  const chapter = Object.keys(value).reduce((acc, curr) => acc < curr ? curr : acc)
  return `${chapter}.${value[chapter]}`
}

// as per middleware, we know that the user param is an uuid
const { data: user } = await useAsyncData(
  `user-${route.params.user}`,
  () => charon<User>(`/users/${route.params.user}`)
    .catch((reason: FetchError) => {
      const responseStatus = reason.response?.status ?? 500
      showError({
        statusCode: responseStatus,
        message: t(`jc.error.userRequest.${responseStatus}.message`, { user: route.params.user }),
      })
    }),
)

const userId = computed(() => user.value ? user.value.id : null)

const { data: userDungeon } = useAsyncData(
  `user-dungeon-${userId.value}`,
  async () => userId.value ? charon<UserDungeon>(`/users/${userId.value}/dungeon`) : null,
  {
    lazy: true,
    watch: [userId],
    server: false,
  },
)

const { data: userVote } = useAsyncData(
  `user-vote-${userId.value}`,
  async () => userId.value ? charon<UserVote>(`/users/${userId.value}/vote`) : null,
  {
    lazy: true,
    watch: [userId],
    server: false,
  },
)

const { data: dungeonEnvironments } = useAsyncData(
  `dungeon-environments`,
  async () => charon<DungeonEnvironment[]>(`/dungeons/environments`),
  {
    lazy: true,
    server: false,
  },
)

useHead(() => {
  const title = user.value ? t(`jc.head.${String(route.meta.nameKey || 'fallback')}.title.withUsername`, { username: user.value?.name }) : undefined
  const description = user.value ? t(`jc.head.${String(route.meta.nameKey || 'fallback')}.description.withUsername`, { username: user.value?.name }) : undefined
  return {
    title: title,
    meta: [
      { name: 'description', content: description },
      // Open Graph Protocol
      { name: 'og:title', content: title },
      { name: 'og:description', content: description },
      { name: 'og:profile:username', content: user.value?.name },
      // Twitter Card
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ],
  }
})

const formatPlaytime = (sec?: number): string | undefined => {
  if (!sec) return undefined

  const d = moment.duration(sec, 's')
  if (d?.minutes() > 60) return t('jc.moment.playtime.minutes', { minutes: d.minutes() })
  return t('jc.moment.playtime.hours', { hours: Math.floor(d.asHours()) })
}
</script>
